import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  PenLine,
  Cpu,
  Rocket,
  Layers,
  GitBranch,
  Gauge,
  FileText,
  ArrowRight,
} from "lucide-react";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { NeuralNetworkBg } from "@/components/NeuralNetworkBg";
import { ScrollReveal } from "@/components/ScrollReveal";
import BlueprintGenerator from "@/components/BlueprintGenerator";
import FAQSection from "@/components/FAQSection";
import { useLocale } from "@/i18n/LocaleProvider";
import { readLastBlueprint } from "@/lib/blueprint";
import { useBlueprintCooldownMinutes } from "@/hooks/usePublicContent";

const BASE_URL = "https://devcore.systems";

const stepIcons = [PenLine, Cpu, Rocket];
const includeIcons = [Layers, GitBranch, FileText, Gauge];

const BlueprintEngine = () => {
  const { t } = useTranslation();
  const { localize } = useLocale();
  const navigate = useNavigate();
  const { data: cooldownMinutes = 10 } = useBlueprintCooldownMinutes();

  // A contact CTA should carry the generated blueprint along (as the attached
  // .md) whenever one exists for this session.
  const goToContact = () => {
    const last = readLastBlueprint();
    navigate(localize("/contact"), { state: last ?? undefined });
  };

  const steps = t("blueprintPage.steps", { returnObjects: true }) as {
    title: string;
    desc: string;
  }[];
  const includes = t("blueprintPage.includes", { returnObjects: true }) as string[];
  const faqs = (
    t("blueprintPage.faqs", { returnObjects: true }) as {
      q: string;
      a: string;
    }[]
  ).map((f) => ({
    q: f.q,
    a: f.a.replace(/\{\{minutes\}\}/g, String(cooldownMinutes)),
  }));



  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        name: "DevCore Blueprint Engine",
        url: `${BASE_URL}${localize("/blueprint")}`,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        description: t("blueprintPage.metaDescription"),
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "EUR",
        },
        provider: {
          "@type": "Organization",
          name: "DevCore",
          url: BASE_URL,
          sameAs: ["https://www.linkedin.com/company/devcore-websystems"],
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: `${BASE_URL}${localize("/")}`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: t("blueprintPage.breadcrumb"),
            item: `${BASE_URL}${localize("/blueprint")}`,
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
    [t, localize, faqs]
  );

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={t("blueprintPage.metaTitle")}
        description={t("blueprintPage.metaDescription")}
        jsonLd={jsonLd}
      />

      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-border pt-36 pb-14 sm:pb-20 md:pt-44 md:pb-28">
          <div className="absolute inset-0 tech-grid opacity-40" />
          <NeuralNetworkBg />
          <div className="absolute inset-0 bg-gradient-to-b from-background/0 via-background/40 to-background" />

          <div className="container relative z-10">
            <div className="max-w-3xl">
              <ScrollReveal>
                <p className="mono-label mb-5 inline-flex items-center gap-2">
                  {t("blueprintPage.eyebrow")}
                </p>
                <h1 className="text-[1.9rem] sm:text-5xl md:text-6xl font-bold text-foreground leading-[1.05] tracking-tight text-balance">
                  {t("blueprintPage.titleA")}{" "}
                  <span className="gradient-text">{t("blueprintPage.titleHighlight")}</span>
                </h1>
                <p className="mt-6 text-base md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
                  {t("blueprintPage.subtitle")}
                </p>
                <ul className="mt-8 flex flex-wrap gap-3">
                  {(t("blueprintPage.badges", { returnObjects: true }) as string[]).map(
                    (b) => (
                      <li
                        key={b}
                        className="font-mono text-xs tracking-[0.12em] uppercase text-muted-foreground border border-border bg-card/50 backdrop-blur-sm rounded-full px-4 py-1.5"
                      >
                        {b}
                      </li>
                    )
                  )}
                </ul>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* The actual generator */}
        <BlueprintGenerator />

        {/* How it works */}
        <section className="relative py-16 sm:py-24 md:py-32 bg-background overflow-hidden">
          <div className="absolute inset-0 tech-grid opacity-[0.08] pointer-events-none" />
          <div className="container relative z-10">
            <ScrollReveal className="max-w-2xl mb-14">
              <p className="mono-label mb-4">// how_it_works</p>
              <h2 className="text-[1.55rem] sm:text-3xl md:text-5xl font-bold text-foreground leading-[1.1] tracking-tight">
                {t("blueprintPage.stepsHeading")}
              </h2>
            </ScrollReveal>

            <div className="grid gap-6 md:grid-cols-3">
              {steps.map((step, i) => {
                const Icon = stepIcons[i] ?? PenLine;
                return (
                  <ScrollReveal key={step.title} delay={i * 100}>
                    <div className="h-full rounded-3xl border border-border bg-card/40 p-7 backdrop-blur-sm">
                      <div className="flex items-center justify-between mb-6">
                        <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-accent">
                          <Icon size={22} />
                        </span>
                        <span className="font-mono text-sm text-muted-foreground/50 tabular-nums">
                          0{i + 1}
                        </span>
                      </div>
                      <h3 className="text-xl font-semibold text-foreground mb-2">
                        {step.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">{step.desc}</p>
                    </div>
                  </ScrollReveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* What's included */}
        <section className="relative py-14 sm:py-20 md:py-28 bg-card text-foreground border-y border-border overflow-hidden">
          <div className="absolute -right-20 top-1/4 w-96 h-96 bg-accent/[0.07] blur-[120px] rounded-full pointer-events-none" />
          <div className="container relative z-10">
            <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
              <ScrollReveal>
                <p className="mono-label mb-4">// proposed_blueprint</p>
                <h2 className="text-[1.55rem] sm:text-3xl md:text-4xl font-bold leading-[1.15] tracking-tight">
                  {t("blueprintPage.includesHeading")}
                </h2>
                <p className="mt-5 text-muted-foreground text-base md:text-lg leading-relaxed">
                  {t("blueprintPage.includesSubtitle")}
                </p>
              </ScrollReveal>
              <ScrollReveal delay={120}>
                <ul className="grid gap-4 sm:grid-cols-2">
                  {includes.map((item, i) => {
                    const Icon = includeIcons[i] ?? Layers;
                    return (
                      <li
                        key={item}
                        className="flex items-start gap-3 rounded-2xl border border-border bg-background/60 p-5"
                      >
                        <span className="mt-0.5 text-accent">
                          <Icon size={20} />
                        </span>
                        <span className="text-foreground font-medium">{item}</span>
                      </li>
                    );
                  })}
                </ul>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQSection
          id="blueprint-faq"
          label="// faq"
          headingA={t("blueprintPage.faqHeading")}
          items={faqs.map((f) => ({ question: f.q, answer: f.a }))}
        />

        {/* Closing CTA */}
        <section className="relative py-16 sm:py-24 md:py-28 bg-card border-t border-border overflow-hidden">
          <div className="absolute -left-20 bottom-0 w-96 h-96 bg-primary/[0.08] blur-[120px] rounded-full pointer-events-none" />
          <div className="container relative z-10 text-center max-w-2xl mx-auto">
            <ScrollReveal>
              <h2 className="text-[1.55rem] sm:text-3xl md:text-5xl font-bold text-foreground leading-[1.1] tracking-tight">
                {t("blueprintPage.ctaTitle")}
              </h2>
              <p className="mt-5 text-muted-foreground text-base md:text-lg leading-relaxed">
                {t("blueprintPage.ctaSubtitle")}
              </p>
              <div className="mt-8 inline-flex hover-glow-accent">
                <button
                  type="button"
                  onClick={goToContact}
                  className="inline-flex items-center gap-2 px-7 py-3.5 btn-clip bg-primary text-primary-foreground btn-label-contrast font-medium transition-transform duration-200 active:scale-[0.97] group"
                >
                  {t("blueprintPage.ctaButton")}
                  <ArrowRight
                    size={18}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </button>
              </div>
            </ScrollReveal>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BlueprintEngine;
